import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { LocalStorageHelpers, SkyAPI } from "@ai360/core";

import { messages } from "../i18n-messages";
import * as actions from "../actions";
import * as selectors from "../selectors";
import { useAppSelector, useAppDispatch } from "~/store/hooks";
import { Button, Checkbox, TextInput } from "~/core";
import { isValidEmail } from "./recover-password";

const { LoginState } = actions;

export interface ILoginForm_Props {
    onError: (error: Error) => void;
    doVersionCheck: () => Promise<boolean>;
}

export const LoginForm = (props: ILoginForm_Props): JSX.Element => {
    const { onError } = props;

    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const error = useAppSelector(selectors.getError);

    const [username, setUsername] = useState<string>(
        LocalStorageHelpers.get(LocalStorageHelpers.USERNAME) || ""
    );
    const [rememberMe, setRememberMe] = useState<boolean>(
        Boolean(LocalStorageHelpers.get(LocalStorageHelpers.REMEMBER_ME))
    );

    const [triggerSsoStatus] = SkyAPI.useLazyGetSsoStatusQuery();
    const [triggerLastConnection] = SkyAPI.useLazyGetSsoLastConnectionQuery();

    // Present-but-hidden password field so password managers recognize this as a
    // login form and offer to fill on the email field. When one fills it, we carry
    // the value into the password step so the user isn't prompted to pick twice.
    const hiddenPasswordRef = useRef<HTMLInputElement>(null);

    async function onContinue() {
        const email = username.replaceAll(" ", "");
        dispatch(actions.setError(null));
        if (email.length === 0) {
            onError(formatMessage(messages.usernameRequired));
            return;
        }
        if (!isValidEmail(email)) {
            onError(formatMessage(messages.validEmail));
            return;
        }
        if (rememberMe) {
            LocalStorageHelpers.set(LocalStorageHelpers.USERNAME, email);
            LocalStorageHelpers.set(LocalStorageHelpers.REMEMBER_ME, "true");
        } else {
            LocalStorageHelpers.remove(LocalStorageHelpers.USERNAME);
            LocalStorageHelpers.remove(LocalStorageHelpers.REMEMBER_ME);
        }
        dispatch(actions.setLoginEmail(email));
        const managerPassword = hiddenPasswordRef.current?.value || "";
        if (managerPassword) {
            dispatch(actions.setLoginPassword(managerPassword));
        }
        dispatch(actions.setProcessing(true));
        try {
            const status = await triggerSsoStatus(email).unwrap();
            if (status?.ssoEnabled) {
                const last = await triggerLastConnection(email).unwrap();
                if (last?.isConnected && last.redirectUrl) {
                    window.location.href = last.redirectUrl;
                    return; // redirecting; keep spinner up
                }
            }
        } catch {
            // 404 / lookup failure → treat as non-SSO, fall through to password
        }
        dispatch(actions.setProcessing(false));
        dispatch(actions.setLoginState(LoginState.PASSWORD_ENTRY));
    }

    function _handleKeyDown(event) {
        if (event.key === "Enter") {
            onContinue();
        }
    }

    useEffect(() => {
        if (error) {
            dispatch(actions.setProcessing(false));
        }
    }, [error]);

    const inputFormProps = {
        onKeyDown: (e) => _handleKeyDown(e),
    };
    return (
        <form onSubmit={(e) => e.preventDefault()}>
            <div className="login-input-container">
                <TextInput
                    tabIndex={1}
                    value={username}
                    autoComplete="username"
                    name="username"
                    placeholderText={formatMessage(messages.usernameLbl)}
                    {...inputFormProps}
                    //removing whitespaces as you type does not trigger re-render on state change
                    onChange={(username) => setUsername(username)}
                    //onBlur does not pass in the text input value, using the value stored by onChange
                    onBlur={() => setUsername(username.replaceAll(" ", ""))}
                />
                {/* Off-screen (not display:none, which managers skip) so managers see a
                    complete username+password login form and offer to fill on email focus. */}
                <input
                    ref={hiddenPasswordRef}
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    tabIndex={-1}
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        left: "-9999px",
                        width: 1,
                        height: 1,
                        opacity: 0,
                    }}
                />
                <div className="login-options-div">
                    <div className="login-remember-me-container">
                        <Checkbox
                            tabIndex={2}
                            value={rememberMe}
                            label={formatMessage(messages.rememberMeLbl)}
                            {...inputFormProps}
                            onChange={(e, rememberMe) => setRememberMe(rememberMe)}
                        />
                    </div>
                </div>
            </div>
            <div className="login-center">
                <Button
                    tabIndex={3}
                    className="login-btn"
                    type="login"
                    {...inputFormProps}
                    onClick={() => onContinue()}
                />
            </div>
        </form>
    );
};

export default LoginForm;
